// Spawn cadence + type distribution + difficulty ramp, verbatim from v1
// js/game3d.js:548-586 and obstacle-manager.js:152-231: 65% cars / 15%
// construction / 20% enemies; coins every 1.5s; power-up roll every 20s;
// +0.01 speed every 10s. Seeded RNG so daily challenges can replay runs.

import {
  OBSTACLE_BASE_INTERVAL,
  OBSTACLE_MIN_INTERVAL,
  COIN_SPAWN_INTERVAL,
  DIFFICULTY_INTERVAL,
  DIFFICULTY_SPEED_STEP,
  MAX_GAME_SPEED,
  CAR_SPAWN_CHANCE,
  POWER_UP_SPAWN_INTERVAL,
} from '@/config/tuning';
import { POWER_UP_IDS, type PowerUpId } from '@/content/powerups';
import type { Rng } from '@/core/rng';
import type { RunState } from '@/app/context';
import type { EventBus } from '@/app/events';
import type { CarSystem } from '@/entities/cars';
import type { CoinSystem } from '@/entities/pickups';
import type { EnemySystem } from '@/entities/enemies/enemy';
import type { ConstructionSystem } from '@/entities/construction';
import type { BarrierSystem } from '@/entities/barriers';
import type { PowerUpSystem } from '@/entities/powerups';

// v1 split 65% cars / 15% construction / 20% enemies; Phase 5 carves the
// slide barriers out of the non-car share: 65 / 13 / 8 / 14
const CONSTRUCTION_CUM = 0.78;
const BARRIER_CUM = 0.86;

export class Spawner {
  private obstacleTimer = 0;
  private coinTimer = 0;
  private difficultyTimer = 0;
  private powerUpTimer = 0;

  constructor(
    private readonly rng: Rng,
    private readonly cars: CarSystem,
    private readonly coins: CoinSystem,
    private readonly enemies: EnemySystem,
    private readonly construction: ConstructionSystem,
    private readonly barriers: BarrierSystem,
    private readonly powerUps: PowerUpSystem,
  ) {}

  update(dt: number, run: RunState, bus: EventBus): void {
    this.obstacleTimer += dt;
    this.coinTimer += dt;
    this.difficultyTimer += dt;
    this.powerUpTimer += dt;

    const interval = Math.max(
      OBSTACLE_MIN_INTERVAL,
      OBSTACLE_BASE_INTERVAL(run.gameSpeed) / run.district.trafficDensity,
    );
    if (this.obstacleTimer > interval) {
      this.obstacleTimer = 0;
      const roll = this.rng.next();
      if (roll < CAR_SPAWN_CHANCE) {
        this.cars.spawn(run.district.baseSpeed, run.district.carSpeedMultiplier);
      } else if (roll < CONSTRUCTION_CUM) {
        this.construction.spawn();
      } else if (roll < BARRIER_CUM) {
        this.barriers.spawn(this.construction);
      } else {
        this.enemies.spawn(run.district.id, (x, z) => this.construction.isPositionBlocked(x, z));
      }
    }

    if (this.coinTimer > COIN_SPAWN_INTERVAL * run.coinIntervalFactor) {
      this.coinTimer = 0;
      this.coins.spawnPattern();
    }

    if (this.difficultyTimer > DIFFICULTY_INTERVAL) {
      this.difficultyTimer = 0;
      run.gameSpeed = Math.min(run.gameSpeed + DIFFICULTY_SPEED_STEP, MAX_GAME_SPEED);
      bus.emit('speed:changed', { gameSpeed: run.gameSpeed });
    }

    if (this.powerUpTimer > POWER_UP_SPAWN_INTERVAL) {
      this.powerUpTimer = 0;
      const typeId = POWER_UP_IDS[Math.floor(this.rng.next() * POWER_UP_IDS.length)] as PowerUpId;
      this.powerUps.spawn(typeId);
    }
  }

  reset(): void {
    this.obstacleTimer = 0;
    this.coinTimer = 0;
    this.difficultyTimer = 0;
    this.powerUpTimer = 0;
  }
}
